import { MRT_ColumnDef, MRT_PaginationState } from "material-react-table";
import { useState } from "react";
import { Box, Button, Chip, IconButton, Typography } from "@mui/material";
import { Delete, Edit, Add } from "@mui/icons-material";
import { DataTable } from "../../../libs/core/components/data-table";
import {
  useGetCategoriesQuery,
  useDeleteCategoryMutation,
} from "../api/galleriesApi";
import { Category } from "../types/gallery";
import { ConfirmModal } from "../../../libs/ui/modal/confirm_modal";
import AddEditCategory from "./AddEditCategory";

const CategoryManager: React.FC = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);

  const { data: categories, isLoading, isError, isFetching } = useGetCategoriesQuery();
  const [deleteCategory] = useDeleteCategoryMutation();

  const columns: Array<MRT_ColumnDef<Category>> = [
    {
      header: "Name",
      accessorKey: "name",
      size: 200,
      Cell({ row: { original } }) {
        return <Typography variant="body2">{original.name}</Typography>;
      },
    },
    {
      header: "Slug",
      accessorKey: "slug",
      size: 200,
      Cell({ row: { original } }) {
        return <Chip label={original.slug} size="small" variant="outlined" />;
      },
    },
    {
      header: "Created At",
      accessorKey: "created_at",
      size: 140,
      Cell({ row: { original } }) {
        return new Date(original.created_at).toLocaleDateString();
      },
    },
  ];

  const handleDelete = async () => {
    if (selectedCategory) {
      try {
        await deleteCategory(selectedCategory.id.toString()).unwrap();
        setOpenConfirmModal(false);
        setSelectedCategory(null);
      } catch (error) {
        console.error("Failed to delete category:", error);
      }
    }
  };

  const handleSuccess = () => {
    setSelectedCategory(null);
  };

  return (
    <Box>
      <DataTable<Category>
        toolbar={
          <Box display="flex" alignItems="center" width="100%">
            <Box display="flex" gap={3} alignItems="center" flex="1">
              <Typography variant="h6">Gallery Categories</Typography>
              <Chip
                color="primary"
                variant="outlined"
                label={`${categories?.data?.total ?? (categories?.data?.length ?? 0)} Categories`}
              />
            </Box>
            <Button
              onClick={() => {
                setSelectedCategory(null);
                setOpenDialog(true);
              }}
              sx={{ minWidth: "200px" }}
              variant="contained"
              startIcon={<Add />}
            >
              Add Category
            </Button>
          </Box>
        }
        list={categories?.data?.data ?? categories?.data ?? []}
        count={categories?.data?.total ?? (categories?.data?.length ?? 0)}
        columns={columns}
        getRowId={(row) => row?.id?.toString()}
        isLoading={isLoading}
        isSuccess={!isError}
        isError={isError}
        isFetching={isFetching}
        renderRowActions={(row) => (
          <Box display={"flex"} gap={1}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedCategory(row);
                setOpenConfirmModal(true);
              }}
              color="error"
            >
              <Delete />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedCategory(row);
                setOpenDialog(true);
              }}
              color="primary"
            >
              <Edit />
            </IconButton>
          </Box>
        )}
        
      />

      <AddEditCategory
        open={openDialog}
        setModalOpen={setOpenDialog}
        onSuccess={handleSuccess}
        category={selectedCategory}
      />

      {selectedCategory && (
        <ConfirmModal
          type="warning"
          title="Delete Category"
          message={`Are you sure you want to delete '${selectedCategory.name}'?`}
          modalOpen={openConfirmModal}
          setModalOpen={setOpenConfirmModal}
          onOk={handleDelete}
        />
      )}
    </Box>
  );
};

export default CategoryManager;
